import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX, RotateCcw } from 'lucide-react';

/**
 * YouTubeEmbed
 * ------------
 * Iframe-API powered embed used across the site. Behaviour:
 *   - The iframe is lazily mounted the first time the wrapper enters the
 *     viewport (~50% visible), so a page with many videos stays light.
 *   - Once mounted, IntersectionObserver play/pauses + restarts the video
 *     as the visitor scrolls toward or away from it.
 *   - Custom Unmute / Restart pills sit at the bottom-left (same visual
 *     language as the homepage hero). A transparent shield blocks YouTube's
 *     hover-only overlays (title pill, share, watch-on-YT, etc.).
 *   - Every video starts MUTED on first mount (browser policy + product
 *     decision). The visitor presses the Unmute pill on each video they
 *     want to hear — the choice is remembered per-video for as long as the
 *     embed stays mounted (so scrolling away and back keeps your last
 *     mute state on that specific video).
 *
 * Props:
 *   - youtubeId    string  required  the 11-char YouTube ID
 *   - title        string  optional  visible caption + iframe a11y title
 *   - description  string  optional  sub-caption below the video
 *   - variant      "default" | "compact" | "card"
 */

const YouTubeEmbed = ({ youtubeId, title, description, variant = "default" }) => {
  const [loaded, setLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef(null);
  const wrapperRef = useRef(null);
  const isMutedRef = useRef(true);
  const inViewRef = useRef(false);

  // Keep ref in sync with state for use inside the IO callback.
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  const sendCommand = useCallback((func, args = '') => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*'
    );
  }, []);

  // IntersectionObserver: mount + play when entering view, pause when leaving.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.intersectionRatio >= 0.5) {
          if (!loaded) {
            // First time entering view → mount the iframe. The URL params
            // already include autoplay=1, so it starts playing on its own.
            setLoaded(true);
            inViewRef.current = true;
            return;
          }
          // Already mounted → restart from the beginning + preserve the
          // visitor's last mute choice on this specific video.
          sendCommand('seekTo', [0, true]);
          sendCommand('playVideo');
          sendCommand(isMutedRef.current ? 'mute' : 'unMute');
          inViewRef.current = true;
        } else if (entry.intersectionRatio < 0.1 && inViewRef.current) {
          inViewRef.current = false;
          sendCommand('pauseVideo');
        }
      },
      { threshold: [0, 0.1, 0.5, 0.75, 1] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sendCommand, loaded]);

  const toggleMute = useCallback(() => {
    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    sendCommand(nextMuted ? 'mute' : 'unMute');
  }, [sendCommand]);

  const handleRestart = useCallback(() => {
    sendCommand('seekTo', [0, true]);
    sendCommand('playVideo');
  }, [sendCommand]);

  if (!youtubeId) return null;

  // All videos always start muted on first mount.
  const videoSrc = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&controls=0&iv_load_policy=3&disablekb=1&fs=0&showinfo=0`;

  const thumbHQ = `https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`;
  const thumbFallback = `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;

  return (
    <figure
      className={`youtube-embed youtube-embed--${variant}`}
      ref={wrapperRef}
      data-testid={`youtube-embed-${youtubeId}`}
    >
      <div className="youtube-embed-frame">
        {loaded ? (
          <>
            <iframe
              ref={iframeRef}
              src={videoSrc}
              title={title || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="youtube-embed-iframe"
              data-testid={`youtube-iframe-${youtubeId}`}
            />
            <div className="hero-video-shield" aria-hidden="true" />
            <div className="hero-video-controls">
              <button
                type="button"
                className={`hero-video-control hero-video-mute ${isMuted ? 'is-muted' : 'is-unmuted'}`}
                onClick={toggleMute}
                aria-label={isMuted ? `Unmute ${title || 'video'}` : `Mute ${title || 'video'}`}
                aria-pressed={!isMuted}
                data-testid={`youtube-mute-toggle-${youtubeId}`}
              >
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                <span className="hero-video-control-label">{isMuted ? 'Unmute' : 'Mute'}</span>
              </button>
              <button
                type="button"
                className="hero-video-control hero-video-restart"
                onClick={handleRestart}
                aria-label={`Restart ${title || 'video'} from the beginning`}
                data-testid={`youtube-restart-${youtubeId}`}
                title="Restart video"
              >
                <RotateCcw size={14} />
              </button>
            </div>
          </>
        ) : (
          <div className="youtube-embed-thumb" aria-hidden="true">
            <img
              src={thumbHQ}
              alt={title || "Video thumbnail"}
              onError={(e) => { if (e.currentTarget.src !== thumbFallback) e.currentTarget.src = thumbFallback; }}
              loading="lazy"
            />
          </div>
        )}
      </div>
      {title || description ? (
        <figcaption className="youtube-embed-caption">
          {title ? <span className="youtube-embed-title">{title}</span> : null}
          {description ? <span className="youtube-embed-desc">{description}</span> : null}
        </figcaption>
      ) : null}
    </figure>
  );
};

export default YouTubeEmbed;
